import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { auditTime, first, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpService } from 'src/app/services/base/http.service';
import { IApiResponse } from 'src/app/models/interfaces/api-response.interface';

/** ตัวเลขงานค้างข้างเมนู — key = ท้าย path ของเมนู (เช่น 'passport-check') · null = นับไม่ได้ ไม่โชว์วงกลม */
export interface IMenuBadges {
    counts: { [menuKey: string]: number | null };
    total: number;
    elapsed_ms: number;
    failed: string[];
}

/**
 * แหล่งเดียวของตัวเลขงานค้างข้างเมนู
 *
 * เดิมหน้า main-theme ยิงเองแล้วรีเฟรชทุก 3 นาที → ปิดงานเสร็จแล้วเลขยังค้างอยู่จนกว่าจะ refresh
 * ทั้งหน้า (user แจ้ง 15 ก.ย. 2569) · ตอนนี้เก็บค่าไว้ที่เดียวแล้วให้ทุกที่สั่ง refresh() ได้
 * — badge-refresh.interceptor สั่งให้เองทุกครั้งที่มีคำสั่งเขียนสำเร็จ จึงไม่ต้องแก้ทีละหน้า
 */
@Injectable({ providedIn: 'root' })
export class MenuBadgeService extends HttpService {
    private base = environment.apiUrl + 'finance/pending';

    /** path ของ endpoint ตัวเอง — ใช้กัน interceptor ยิงวนซ้ำ */
    static readonly ENDPOINT = 'finance/pending/menu_badges';

    private state = new BehaviorSubject<{ [menuKey: string]: number | null }>({});
    /** ตัวเลขล่าสุด — subscribe แล้วได้ค่าปัจจุบันทันที */
    readonly counts$: Observable<{ [menuKey: string]: number | null }> = this.state.asObservable();

    /** คำขอรีเฟรช — รวบให้เหลือครั้งเดียวต่อ 800 มิลลิวินาที (กดปิดงานรัว ๆ ไม่ยิงถล่ม) */
    private refreshRequests = new Subject<void>();
    private started = false;

    constructor(
        @Inject(PLATFORM_ID) protected platformId: Object,
        protected http: HttpClient,
        protected router: Router
    ) {
        super(platformId, http, router);
    }

    counts(): Observable<IApiResponse<IMenuBadges>> {
        return this.get(`${this.base}/menu_badges`, true);
    }

    /** ขอให้ไปนับใหม่ — เรียกซ้ำได้ ระบบรวบให้เอง */
    refresh(): void {
        this.ensureStarted();
        this.refreshRequests.next();
    }

    /**
     * ลดเลขทันทีแบบไม่รอ API (ตอนรู้อยู่แล้วว่าเพิ่งปิดงานไปกี่ชิ้น)
     * ใช้คู่กับ refresh() เสมอ — ตัวนี้ทำให้เห็นผลทันตา ส่วน refresh() คือค่าจริงที่ตามมา
     */
    bump(menuKey: string, delta: number): void {
        const cur = this.state.value;
        const n = cur[menuKey];
        if (typeof n !== 'number') return;
        this.state.next({ ...cur, [menuKey]: Math.max(0, n + delta) });
    }

    private ensureStarted(): void {
        if (this.started) return;
        this.started = true;
        this.refreshRequests
            .pipe(
                auditTime(800),
                switchMap(() => this.counts().pipe(first()))
            )
            .subscribe({
                next: (res) => this.state.next(res.data?.counts || {}),
                error: () => {}, // นับไม่ได้ = คงเลขเดิมไว้ ไม่รบกวนการใช้งาน
            });
    }
}
